import React, { useEffect, useState } from "react";

import { Grid, IconButton, MenuItem, SelectChangeEvent } from "@mui/material";
import { Add, DirectionsBoatOutlined as DirectionsBoatOutlinedIcon } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { CiCircleMinus, CiCirclePlus } from "react-icons/ci";
import { IoAirplaneOutline } from "react-icons/io5";
import { RiTruckLine } from "react-icons/ri";
import { v4 as uuid } from "uuid";

import { useAppState } from "../../../../context/StateContext";
import { Destination } from "../../../../models/destination";
import { JobMode } from "../../../../models/job.mode";
import { DifferentLocation as LocationAddress } from "../../../../models/location";
import { Material } from "../../../../models/material";
import { ItemJob, QuotedItem } from "../../../../models/sales";
import { ServiceBooking } from "../../../../models/service.booking";
import { SpecialInstruction } from "../../../../models/special.instruction";
import ModeJobService from "../../../../services/mode.job.service";
import SpecialInstructionService from "../../../../services/special.instruction.service";
import { DESTINATION, DisassemblyOptions, Mode, Status } from "../../../../util/enums";
import formatAllMaterialsFromItem from "../../../../util/formatAllMaterialsFromItem";
import { getValueFromEachDestination } from "../../../../util/getDestinations";
import { getMeasurement, getWeightMeasurement } from "../../../../util/getMeasurement";
import { isAllied } from "../../../../util/isAllied";
import { Button, Input, Select } from "../../../Form";
import { Color } from "../../../Form/Button";
import InputRange from "../../../Form/InputRange";
import RadioGroup from "../../../Form/RadioGroup";
import Modal from "../../Modal";
import SkeletonComponent from "../../Skeleton";
import FormMaterial from "./FormMaterial";
import SpecialInstructionsContent from "./SpecialInstructionsContent";
import useStyles, { SectionWrap } from "./styles";

interface EditItemModalProps {
  oldQuotedItem: QuotedItem | null;
  openModal: boolean;
  destinations: Destination[];
  locations: LocationAddress[];
  closeModal: () => void;
  submit: (item: QuotedItem) => void;
  packingMaterials?: ServiceBooking[];
}

type UpdateItemProps = {
  attribute:
    | "width"
    | "volume"
    | "depth"
    | "height"
    | "quantity"
    | "weight"
    | "comments"
    | "packer";
  value: number | string;
  additionalUpdates?: Pick<SpecialInstruction, "volume">;
};

const EditItemModal = ({
  packingMaterials,
  oldQuotedItem,
  openModal,
  destinations,
  closeModal,
  locations,
  submit,
}: EditItemModalProps) => {
  const { currentUser } = useAppState();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const [itemSelected, setItemSelected] = useState<QuotedItem | null>();

  const [selectedInstruction, setSelectedInstruction] = useState<SpecialInstruction[]>([]);

  const [jobModeList, setJobModeList] = useState<JobMode[]>([]);

  const [itemName, setItemName] = useState<string>(oldQuotedItem?.itemJob.description || "");

  const [instruction, setInstruction] = useState<string>("");

  const [materialsToAdd, setMaterialsToAdd] = useState<Material[]>([]);

  const classes = useStyles();

  const { t: translate } = useTranslation();

  const disassemblyOptions = [
    {
      label: DisassemblyOptions.REMOVALIST,
      value: DisassemblyOptions.REMOVALIST,
    },
    {
      label: DisassemblyOptions.CUSTOMER,
      value: DisassemblyOptions.CUSTOMER,
    },
  ];

  const { data: specialInstructions } = useQuery(
    ["special.instructionOptions.get"],
    () =>
      SpecialInstructionService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify(["description"]),
      }),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { isFetching: isFetchingMode } = useQuery(
    ["jobMode.list"],
    () =>
      ModeJobService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
      }),
    {
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setJobModeList(response.data);
        }
      },
    }
  );

  const handleDeleteInstruction = (id: number) => {
    const arr = selectedInstruction;
    const idx = arr
      .map((item: SpecialInstruction, index: number) => (item.id === id ? index : -1))
      .filter((value: number) => value !== -1)[0];

    arr.splice(idx, 1);

    setSelectedInstruction([...arr]);
  };

  const handleSubmitChanges = () => {
    if (itemSelected) {
      let itemUpdated = {
        ...itemSelected,
        itemJob: {
          ...itemSelected.itemJob,
          packer: itemSelected?.itemJob?.packer
            ? itemSelected?.itemJob?.packer
            : DisassemblyOptions.REMOVALIST,
          specialInstructions: selectedInstruction,
        },
      };

      if (
        oldQuotedItem?.itemJob?.destination?.name?.toLowerCase() ===
          DESTINATION.NOT_GOING?.toLowerCase() &&
        itemUpdated?.itemJob?.destination?.name?.toLowerCase() !==
          DESTINATION.NOT_GOING?.toLowerCase() &&
        oldQuotedItem?.itemJob?.item?.packingMaterials &&
        oldQuotedItem?.itemJob?.item?.packingMaterials?.length > 0 &&
        (!itemUpdated?.itemJob?.packingMaterials ||
          itemUpdated?.itemJob?.packingMaterials?.length === 0)
      ) {
        itemUpdated = {
          ...itemUpdated,
          itemJob: {
            ...(itemUpdated?.itemJob || {}),
            packingMaterials: formatAllMaterialsFromItem(
              oldQuotedItem?.itemJob?.item?.packingMaterials,
              itemUpdated?.itemJob
            ),
          },
        };
      }

      setItemSelected(itemUpdated);
      submit(itemUpdated);
    }
    closeModal();

    setSelectedInstruction([]);
  };

  useEffect(() => {
    setItemSelected(oldQuotedItem);
    setSelectedInstruction(oldQuotedItem?.itemJob?.specialInstructions || []);
    setItemName(oldQuotedItem?.itemJob?.description || "");
  }, [oldQuotedItem]);

  const isActive = (mode: string): boolean => mode === itemSelected?.itemJob?.mode;

  const renderModeItem = (title: string, isActive: boolean, icon?: JSX.Element, label?: string) => {
    const updateModeAndDestination = () => {
      if (!itemSelected) return;
      const modeFreight: string = `${title} freight`;

      const destinationFoundByName =
        destinations && destinations?.length > 0
          ? destinations?.find(
              (destination) => destination?.id && destination?.name === modeFreight
            )
          : null;

      let itemJob: ItemJob = { ...itemSelected?.itemJob, mode: title };

      let itemMaterials: Material[] = [];
      let changeDestinationObject = {};

      if (
        destinationFoundByName &&
        destinationFoundByName?.id &&
        !destinationFoundByName?.destinationResidenceId &&
        !destinationFoundByName?.modeDestinationId
      ) {
        changeDestinationObject = {
          destination:
            destinationFoundByName &&
            destinationFoundByName?.id &&
            !destinationFoundByName?.destinationResidenceId &&
            !destinationFoundByName?.modeDestinationId
              ? destinationFoundByName
              : undefined,
          destinationId:
            destinationFoundByName &&
            destinationFoundByName?.id &&
            !destinationFoundByName?.destinationResidenceId &&
            !destinationFoundByName?.modeDestinationId
              ? destinationFoundByName?.id
              : null,
          destinationResidenceId: null,
          destinationResidence: undefined,
          modeDestination: undefined,
          modeDestinationId: null,
        };

        itemJob = {
          ...itemJob,
          ...changeDestinationObject,
        } as ItemJob;
      }

      if (itemJob?.packingMaterials && itemJob?.packingMaterials?.length > 0) {
        itemMaterials = itemJob?.packingMaterials?.map((material) => {
          return { ...material, mode: title, ...changeDestinationObject };
        });
      }

      setItemSelected({
        ...itemSelected,
        itemJob: {
          ...itemJob,
          packingMaterials: itemMaterials,
        },
        pictures: itemSelected.pictures,
      });
    };

    return (
      <div
        key={title}
        className={classnames(
          classes.modalEditItemModeBox,
          isActive ? classes.modalEditItemModeBoxActive : ""
        )}
        onClick={() => updateModeAndDestination()}
      >
        {icon && icon}
        <span className={classnames(classes.titleMode, isActive ? classes.titleModeActive : "")}>
          {label || title}
        </span>
      </div>
    );
  };

  const getDestinationByItemSelected = () => {
    if (
      itemSelected?.itemJob &&
      itemSelected?.itemJob?.destination &&
      itemSelected?.itemJob?.destinationId
    ) {
      return itemSelected?.itemJob?.destination?.name;
    }

    if (
      itemSelected?.itemJob &&
      itemSelected?.itemJob?.modeDestination &&
      itemSelected?.itemJob?.modeDestinationId
    ) {
      return itemSelected?.itemJob?.modeDestination?.name;
    }

    if (
      itemSelected?.itemJob &&
      itemSelected?.itemJob?.destinationResidence &&
      itemSelected?.itemJob?.destinationResidenceId
    ) {
      return `Residence: ${itemSelected?.itemJob?.destinationResidence?.longAddress}`;
    }

    return "";
  };

  const getItemModeByDestination = (destination?: Destination) => {
    if (destination) {
      let mode = "";

      if (destination?.name?.toLowerCase().includes("freight")) {
        const modeSplit = destination?.name?.split(" ");

        if (modeSplit && modeSplit.length > 0) {
          const [destinationName] = modeSplit;

          mode = destinationName;
        }
      }

      if (isAllied(currentUser?.company)) {
        if (destination?.destinationResidenceId) {
          return Mode.Road;
        }
      }

      if (mode) return mode;
    }

    return itemSelected && itemSelected?.itemJob?.mode ? itemSelected?.itemJob?.mode : undefined;
  };

  const getModeIcon = (title: string) => {
    switch (title) {
      case Mode.Road:
        return (
          <RiTruckLine className={classes.icon} color={isActive(title) ? "#308CDB" : "#93A9BD"} />
        );
      case Mode.Sea:
        return (
          <DirectionsBoatOutlinedIcon
            className={classes.icon}
            htmlColor={isActive(title) ? "#308CDB" : "#93A9BD"}
          />
        );
      case Mode.Air:
        return (
          <IoAirplaneOutline
            className={classes.icon}
            color={isActive(title) ? "#308CDB" : "#93A9BD"}
          />
        );
      default:
        return undefined;
    }
  };

  const onSave = (material: ServiceBooking, quantity: number) => {
    if (itemSelected?.itemJob) {
      const materialsFormatted = formatAllMaterialsFromItem(
        [material],
        itemSelected?.itemJob,
        quantity
      );

      if (materialsFormatted && materialsFormatted?.length > 0) {
        setItemSelected({
          ...itemSelected,
          itemJob: {
            ...itemSelected.itemJob,
            packingMaterials: [
              ...(itemSelected?.itemJob?.packingMaterials || []),
              ...materialsFormatted,
            ],
          },
        });
      }
    }
  };

  const removeMaterialToAdd = (materialRemove: Material) => {
    if (materialsToAdd && materialsToAdd?.length > 0) {
      const materialsFiltered = materialsToAdd.filter(
        (material) => material?.uuid !== materialRemove?.uuid
      );

      setMaterialsToAdd(materialsFiltered);
    }
  };

  const onIncrease = (index: number) => {
    if (
      itemSelected?.itemJob?.packingMaterials &&
      itemSelected?.itemJob?.packingMaterials?.length > 0
    ) {
      const packingMaterialsUpdated = [...(itemSelected?.itemJob?.packingMaterials || [])];

      const oldQuantity = packingMaterialsUpdated[index]?.quantity || 1;

      const packingMaterial = {
        ...(packingMaterialsUpdated[index] || {}),
        quantity: oldQuantity + 1,
      } as Material;

      packingMaterialsUpdated[index] = packingMaterial;

      setItemSelected({
        ...itemSelected,
        itemJob: {
          ...(itemSelected?.itemJob || {}),
          packingMaterials: packingMaterialsUpdated,
        },
      });
    }
  };

  const onDecreaseOrRemove = (index: number) => {
    if (
      itemSelected?.itemJob?.packingMaterials &&
      itemSelected?.itemJob?.packingMaterials?.length > 0
    ) {
      const packingMaterialsUpdated = [...(itemSelected?.itemJob?.packingMaterials || [])];
      const packingMaterialsDeletedUpdated = [
        ...(itemSelected?.itemJob?.packingMaterialsDeleted || []),
      ];

      const oldQuantity = packingMaterialsUpdated[index]?.quantity || 1;
      const newQuantity = oldQuantity - 1;

      if (newQuantity < 1) {
        const packingMaterialDelete = packingMaterialsUpdated[index];

        if (packingMaterialDelete?.id) {
          packingMaterialsDeletedUpdated.push(packingMaterialDelete);
        }

        packingMaterialsUpdated.splice(index, 1);
      } else {
        packingMaterialsUpdated[index] = {
          ...(packingMaterialsUpdated[index] || {}),
          quantity: newQuantity,
        } as Material;
      }

      setItemSelected({
        ...itemSelected,
        itemJob: {
          ...(itemSelected?.itemJob || {}),
          packingMaterials: packingMaterialsUpdated,
          packingMaterialsDeleted: packingMaterialsDeletedUpdated,
        },
      });
    }
  };

  const renderListLinkMaterials = () => {
    if (
      itemSelected?.itemJob?.packingMaterials &&
      itemSelected?.itemJob?.packingMaterials?.length > 0
    ) {
      return itemSelected?.itemJob?.packingMaterials?.map((material, index) => {
        return (
          <div key={material?.id || material?.uuid} className={classes.linkContainer}>
            <IconButton onClick={() => onDecreaseOrRemove(index)} className={classes.iconButton}>
              <CiCircleMinus size={26} />
            </IconButton>
            <span className={classes.quantityLinkedMaterial}>{material?.quantity}</span>
            <IconButton onClick={() => onIncrease(index)} className={classes.iconButton}>
              <CiCirclePlus size={26} />
            </IconButton>
            <span className={classes.titleMaterial}>{material?.serviceBooking?.name}</span>
          </div>
        );
      });
    }

    return null;
  };

  const renderListFormMaterials = () => {
    if (materialsToAdd && materialsToAdd?.length > 0) {
      return materialsToAdd?.map((material) => {
        return (
          <FormMaterial
            key={material?.uuid}
            onSave={(serviceBooking, quantity) => {
              onSave(serviceBooking, quantity);
              removeMaterialToAdd(material);
            }}
            onCancel={() => removeMaterialToAdd(material)}
            packingMaterials={packingMaterials}
            roomId={itemSelected?.itemJob?.roomId}
          />
        );
      });
    }

    return null;
  };

  const renderContainerLinkMaterials = () => {
    if (
      itemSelected?.itemJob?.packingMaterials &&
      itemSelected?.itemJob?.packingMaterials?.length > 0
    ) {
      return <div className={classes.containerMaterialAdd}>{renderListLinkMaterials()}</div>;
    }

    return null;
  };

  const renderContainerMaterialAdd = () => {
    if (materialsToAdd && materialsToAdd?.length > 0) {
      return <div className={classes.containerMaterialAdd}>{renderListFormMaterials()}</div>;
    }

    return null;
  };

  return (
    <Modal
      open={openModal}
      handleClose={closeModal}
      data-cy="editItemModal"
      title={translate("inventory")}
      showActionButtons
      actionButtonProps={{
        onSave: handleSubmitChanges,
        dataCySave: "submitChanges",
        onCancel: () => {
          closeModal();
          setSelectedInstruction([]);
        },
        saveDisabled: !!(
          !itemSelected?.itemJob?.destinationId &&
          !itemSelected?.itemJob?.destinationResidenceId &&
          !itemSelected?.itemJob?.modeDestinationId
        ),
      }}
    >
      <span className={classes.modalEditItemBodyTitle}>{translate("itemDetails")}</span>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Input
            fullWidth
            name="description"
            value={itemName}
            label={translate("description")}
            onChange={(event) => {
              setItemName(event.target.value);
              if (itemSelected)
                setItemSelected({
                  ...itemSelected,
                  itemJob: {
                    ...itemSelected.itemJob,
                    description: event.target.value,
                  },
                  pictures: itemSelected.pictures,
                });
            }}
          />
        </Grid>
        {!isAllied(currentUser?.company) && (
          <Grid item xs={12}>
            <span className={classes.modalEditItemModeLabel}>{translate("mode")}</span>

            <SkeletonComponent isLoading={isFetchingMode}>
              <SectionWrap gap="16px">
                {jobModeList.map((mode) => {
                  return renderModeItem(mode.name, isActive(mode.name), getModeIcon(mode.name), translate(mode.value.toLowerCase()) || mode.name);
                })}
              </SectionWrap>
            </SkeletonComponent>
          </Grid>
        )}
        <Grid item xs={12}>
          <SectionWrap gap="32px">
            <InputRange
              decimalPlacesMaxLength={0}
              label={translate("quantity")}
              value={itemSelected?.itemJob?.quantity || 0}
              change={(value: number) =>
                itemSelected &&
                setItemSelected({
                  ...itemSelected,
                  itemJob: { ...itemSelected.itemJob, quantity: value },
                  pictures: itemSelected.pictures,
                })
              }
              range={1}
              onlyIntValues
            />
            <InputRange
              label={translate("volume")}
              value={itemSelected?.itemJob?.volume || 0}
              measurementInputRange={measurement}
              change={(value: number) =>
                itemSelected &&
                setItemSelected({
                  ...itemSelected,
                  itemJob: {
                    ...itemSelected.itemJob,
                    volume: value,
                  },
                  pictures: itemSelected.pictures,
                })
              }
              minValue={0}
            />
            <InputRange
              label={translate("weight")}
              measurementInputRange={{
                code: measurementWeight?.code || "kg",
                decimalPlaces: measurementWeight?.decimalPlaces,
              }}
              value={itemSelected?.itemJob?.weight || 0}
              minValue={0}
              change={(value: number) => {
                if (itemSelected) {
                  setItemSelected({
                    ...itemSelected,
                    itemJob: {
                      ...itemSelected.itemJob,
                      weight: value,
                    },
                  });
                }
              }}
            />
          </SectionWrap>
        </Grid>

        <Grid item xs={12}>
          <Select
            data-cy="destinationModalSelect"
            name="destination"
            label={translate("destination")}
            value={getDestinationByItemSelected()}
            onChange={(event) => {
              if (!itemSelected) return;
              const destination = destinations?.find(
                (destination) => destination.name === event.target.value
              );

              const objectChangeDestination = {
                destination: getValueFromEachDestination(destination)?.destination,
                destinationId: getValueFromEachDestination(destination)?.destination?.id || null,

                destinationResidence: getValueFromEachDestination(destination, locations)
                  ?.destinationResidence,
                destinationResidenceId:
                  getValueFromEachDestination(destination, locations)?.destinationResidence?.id ||
                  null,

                modeDestination: getValueFromEachDestination(destination)?.modeDestination,
                modeDestinationId:
                  getValueFromEachDestination(destination)?.modeDestination?.id || null,

                mode: getItemModeByDestination(destination),
              };

              let itemMaterials: Material[] = [];

              if (
                itemSelected?.itemJob?.packingMaterials &&
                itemSelected?.itemJob?.packingMaterials?.length > 0
              ) {
                itemMaterials = itemSelected?.itemJob?.packingMaterials?.map((material) => {
                  return { ...material, ...objectChangeDestination };
                });
              }

              setItemSelected({
                ...itemSelected,
                itemJob: {
                  ...itemSelected.itemJob,
                  ...objectChangeDestination,
                  packingMaterials: itemMaterials,
                },
                pictures: itemSelected.pictures,
              });
            }}
            placeholder={translate("chooseADestination")}
          >
            {destinations &&
              destinations?.map((destination: Destination, index: number) => (
                <MenuItem
                  data-cy={`${index}-destination-option`}
                  key={destination?.name || index}
                  value={destination?.name}
                >
                  {destination?.name}
                </MenuItem>
              ))}
          </Select>
        </Grid>
        <Grid item xs={12}>
          <RadioGroup
            row
            label="Packed By:"
            aria-label="by"
            name="byCustomer"
            value={itemSelected?.itemJob.packer || DisassemblyOptions.REMOVALIST}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              itemSelected &&
              setItemSelected({
                ...itemSelected,
                itemJob: {
                  ...itemSelected.itemJob,
                  packer: e.target.value,
                },
                pictures: itemSelected.pictures,
              })
            }
            options={disassemblyOptions}
          />
        </Grid>
        <Grid item xs={12}>
          <Input
            label={translate("comments")}
            value={itemSelected?.itemJob.comments || ""}
            multiline
            maxRows={5}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              itemSelected &&
              setItemSelected({
                ...itemSelected,
                itemJob: { ...itemSelected.itemJob, comments: e.target.value },
                pictures: itemSelected.pictures,
              })
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Select
            name="instructions"
            label={translate("itemInstructions")}
            onChange={(event: SelectChangeEvent<any>) => {
              if (event.target.value !== "") {
                setInstruction(event.target.value);
              }
            }}
            displayEmpty
            placeholder={translate("chooseAnItemInstruction")}
            value={instruction}
          >
            {specialInstructions?.data.map((instruction) => (
              <MenuItem key={instruction.description} value={instruction.description}>
                {instruction.description}
              </MenuItem>
            ))}
          </Select>
          {selectedInstruction.length > 0 && (
            <div>
              {selectedInstruction.map((item: SpecialInstruction, index: number) => (
                <div key={item.name || index}>
                  <SpecialInstructionsContent
                    instruction={item}
                    deleteItem={() => item.id && handleDeleteInstruction(item.id)}
                    updateItem={({ attribute, value, additionalUpdates }: UpdateItemProps) => {
                      const itemToUpdate = selectedInstruction[index];

                      if (itemToUpdate) {
                        const item: SpecialInstruction = {
                          ...itemToUpdate,
                          ...additionalUpdates,
                          [attribute]: value === "" ? undefined : value,
                        };

                        selectedInstruction[index] = item;
                        setSelectedInstruction([...selectedInstruction]);
                      }
                    }}
                  />
                  <hr className={classes.separator} />
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 text-right">
            <Button
              disabled={instruction === ""}
              startIcon={<Add />}
              onClick={() => {
                if (instruction !== "")
                  setSelectedInstruction([
                    ...selectedInstruction,
                    {
                      name: instruction,
                      id: Math.floor(Math.random() * 200) + 1,
                    },
                  ]);
              }}
              customColor={Color.SECONDARY}
            >
              {selectedInstruction.length === 0 ? translate("add") : translate("addAnother")}
            </Button>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className={classes.containerTitle}>
            <span className={classnames(classes.titleMaterial, classes.titleLinkedMaterial)}>
              {translate("linkedMaterials")}
            </span>
          </div>
          {renderContainerLinkMaterials()}
          <div
            className={
              itemSelected?.itemJob?.packingMaterials &&
              itemSelected?.itemJob?.packingMaterials?.length > 0
                ? classes.containerButtonMaterial
                : ""
            }
          >
            {renderContainerMaterialAdd()}
            <Button
              className={
                materialsToAdd && materialsToAdd?.length > 0
                  ? classnames(classes.buttonAddMaterial, classes.containerButtonMaterial)
                  : classes.buttonAddMaterial
              }
              startIcon={<Add />}
              onClick={() => {
                setMaterialsToAdd((prev) => [...prev, { uuid: uuid() } as Material]);
              }}
              customColor={Color.SECONDARY_OUTLINED}
            >
              {translate("addMaterial")}
            </Button>
          </div>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default EditItemModal;
